import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { GroupRefresh } from '../components/group-refresh';

interface ForumGroupsResponse {
  groupMap: Record<string, string>;
  source: string;
  lastRefreshedAt?: string;
}

export function GroupsPage() {
  const { data, isLoading, error } = useQuery<ForumGroupsResponse>({
    queryKey: ['forum-groups'],
    queryFn: ({ signal }) => api<ForumGroupsResponse>('/api/forum-groups', { signal }),
    staleTime: 5 * 60_000,
  });

  const groupMap = data?.groupMap ?? {};
  const entries = Object.entries(groupMap)
    .map(([id, name]) => ({ id: Number(id), name }))
    .sort((a, b) => a.id - b.id);

  return (
    <div>
      <div className="eo-eyebrow">Authentication · Forum Groups</div>
      <div className="flex items-center justify-between mb-5">
        <h1 className="eo-h1">Group Registry</h1>
        <GroupRefresh />
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-2.5 mb-6">
          <div className="eo-kpi">
            <div className="eo-kpi__n">{entries.length}</div>
            <div className="eo-kpi__l">Forum Groups</div>
          </div>
          <div className="eo-kpi">
            <div className="eo-kpi__n">{data.source}</div>
            <div className="eo-kpi__l">Source</div>
          </div>
          <div className="eo-kpi">
            <div className="eo-kpi__n">
              {data.lastRefreshedAt
                ? new Date(data.lastRefreshedAt).toLocaleDateString()
                : '—'}
            </div>
            <div className="eo-kpi__l">Last Refreshed</div>
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-ink-3">Loading groups…</p>}
      {error && (
        <p className="text-sm text-err">
          Failed to load: {(error as Error).message}
        </p>
      )}

      {data && (
        <section>
          <h2 className="eo-h2">
            ◆ All Groups
            <span className="text-ink-4 ml-2">{entries.length}</span>
          </h2>
          <table className="eo-table">
            <thead>
              <tr>
                <th className="w-20">Group ID</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((g) => (
                <tr key={g.id}>
                  <td>
                    <span className="eo-mono">{g.id}</span>
                  </td>
                  <td className="font-medium text-ink-1">{g.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
